import React, { useState, useEffect, useContext, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { UserContext, API } from "../App";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { Textarea } from "../components/ui/textarea";
import { ArrowLeft, Plus, Trash2, Edit, Check, X, Sparkles, Upload, RefreshCw } from "lucide-react";
import { toast } from "sonner";

const ADMIN_EMAILS = ["andrebravodj@gmail.com", "eternalnow@eternalnow.life"];

const emptyQuote = {
  text: "",
  author: "",
  source: "",
  category: "consciousness",
  tags: "",
  mood: "mystical",
  language: "english",
  cinematic_score: 80,
  is_public: false,
};

const AdminPage = () => {
  const navigate = useNavigate();
  const { user, userLoading } = useContext(UserContext);

  const [stats, setStats] = useState(null);
  const [categories, setCategories] = useState([]);
  const [moods, setMoods] = useState([]);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  // Filters
  const [filter, setFilter] = useState({
    category: "all",
    mood: "all",
    is_public: "all",
    is_used: "all",
    search: "",
  });

  // Editor state
  const [editing, setEditing] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [showBulk, setShowBulk] = useState(false);
  const [bulkText, setBulkText] = useState("");
  const [generateCount, setGenerateCount] = useState(20);
  const [generating, setGenerating] = useState(false);

  const authHeader = useCallback(
    () => ({ Authorization: `Bearer ${localStorage.getItem("token")}` }),
    []
  );

  // ===== Access check =====
  useEffect(() => {
    if (userLoading) return;
    if (!user) {
      navigate("/");
      return;
    }
    if (!ADMIN_EMAILS.includes(user.email?.toLowerCase())) {
      toast.error("Admin access required");
      navigate("/");
    }
  }, [user, userLoading, navigate]);

  // ===== Data fetchers =====
  const fetchStats = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/stats`, { headers: authHeader() });
      setStats(res.data);
    } catch (e) {
      console.error("fetchStats", e);
    }
  }, [authHeader]);

  const fetchCategories = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/categories`, { headers: authHeader() });
      setCategories(res.data.categories || []);
      setMoods(res.data.moods || []);
    } catch (e) {
      console.error("fetchCategories", e);
    }
  }, [authHeader]);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filter.category !== "all") params.category = filter.category;
      if (filter.mood !== "all") params.mood = filter.mood;
      if (filter.is_public !== "all") params.is_public = filter.is_public === "true";
      if (filter.is_used !== "all") params.is_used = filter.is_used === "true";
      if (filter.search) params.search = filter.search;
      const res = await axios.get(`${API}/admin/quotes`, { params, headers: authHeader() });
      setItems(res.data.items || []);
      setTotal(res.data.total || 0);
    } catch (e) {
      console.error("fetchList", e);
      toast.error("Failed to load quotes");
    } finally {
      setLoading(false);
    }
  }, [authHeader, filter]);

  useEffect(() => {
    if (user && ADMIN_EMAILS.includes(user.email?.toLowerCase())) {
      fetchStats();
      fetchCategories();
      fetchList();
    }
  }, [user, fetchStats, fetchCategories, fetchList]);

  // ===== Mutations =====
  const saveQuote = async (data, id = null) => {
    try {
      const payload = {
        ...data,
        cinematic_score: Number(data.cinematic_score),
        tags: typeof data.tags === "string"
          ? data.tags.split(",").map((t) => t.trim()).filter(Boolean)
          : data.tags,
      };
      if (id) {
        await axios.put(`${API}/admin/quotes/${id}`, payload, { headers: authHeader() });
        toast.success("Quote updated");
      } else {
        await axios.post(`${API}/admin/quotes`, payload, { headers: authHeader() });
        toast.success("Quote created");
      }
      setEditing(null);
      setShowCreate(false);
      fetchList();
      fetchStats();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    }
  };

  const deleteQuote = async (id) => {
    if (!window.confirm("Delete this quote?")) return;
    try {
      await axios.delete(`${API}/admin/quotes/${id}`, { headers: authHeader() });
      toast.success("Deleted");
      fetchList();
      fetchStats();
    } catch (e) {
      toast.error("Delete failed");
    }
  };

  const setPublic = async (id, isPublic) => {
    try {
      const endpoint = isPublic ? "approve" : "reject";
      await axios.post(`${API}/admin/quotes/${id}/${endpoint}`, {}, { headers: authHeader() });
      toast.success(isPublic ? "Approved" : "Rejected");
      fetchList();
      fetchStats();
    } catch (e) {
      toast.error("Action failed");
    }
  };

  const toggleUsed = async (id, isUsed) => {
    try {
      await axios.put(
        `${API}/admin/quotes/${id}`,
        { is_used: !isUsed },
        { headers: authHeader() }
      );
      fetchList();
      fetchStats();
    } catch (e) {
      toast.error("Update failed");
    }
  };

  const bulkImport = async () => {
    try {
      const parsed = JSON.parse(bulkText);
      if (!Array.isArray(parsed)) {
        toast.error("Bulk JSON must be an array");
        return;
      }
      const res = await axios.post(
        `${API}/admin/quotes/bulk`,
        { quotes: parsed },
        { headers: authHeader() }
      );
      toast.success(`Imported ${res.data.inserted}, skipped ${res.data.skipped_duplicates} duplicates`);
      setShowBulk(false);
      setBulkText("");
      fetchList();
      fetchStats();
    } catch (e) {
      toast.error(e.message || "Bulk import failed");
    }
  };

  const generateDrafts = async () => {
    setGenerating(true);
    try {
      const res = await axios.post(
        `${API}/admin/quotes/generate`,
        { count: Number(generateCount) },
        { headers: authHeader(), timeout: 120000 }
      );
      toast.success(`Generated ${res.data.inserted} drafts (${res.data.rejected_duplicates} duplicates, ${res.data.rejected_invalid} invalid)`);
      fetchList();
      fetchStats();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Generation failed");
    } finally {
      setGenerating(false);
    }
  };

  // ===== Render =====
  if (userLoading || !user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-black">
        <div className="text-amber-200/80 text-sm pulse uppercase tracking-[0.4em] cinzel">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white cosmos-bg relative">
      {/* Nav */}
      <nav className="fixed top-0 w-full z-50 bg-black/80 backdrop-blur-sm border-b border-amber-200/15">
        <div className="max-w-7xl mx-auto px-8 py-6 flex items-center justify-between">
          <div className="flex items-center space-x-8">
            <button
              data-testid="admin-back-btn"
              onClick={() => navigate("/")}
              className="text-xs uppercase tracking-[0.25em] text-amber-200/80 hover:text-amber-200 transition-colors font-light flex items-center"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Home
            </button>
            <span className="script-font text-3xl gold-text">Admin</span>
          </div>
          <span className="text-[10px] uppercase tracking-[0.3em] text-amber-200/50 cinzel">
            Content Engine
          </span>
        </div>
      </nav>

      <div className="relative z-10 pt-32 pb-16 px-8 max-w-7xl mx-auto">
        {/* Stats */}
        {stats && <StatsRow stats={stats} />}

        <Tabs defaultValue="quotes" className="mt-8">
          <TabsList className="bg-zinc-950 border border-amber-200/25 mb-8">
            <TabsTrigger value="quotes" data-testid="tab-quotes" className="data-[state=active]:gold-btn text-xs uppercase tracking-[0.25em] cinzel">
              Quotes
            </TabsTrigger>
            <TabsTrigger value="generate" data-testid="tab-generate" className="data-[state=active]:gold-btn text-xs uppercase tracking-[0.25em] cinzel">
              <Sparkles className="w-3 h-3 mr-2" />
              Generate
            </TabsTrigger>
            <TabsTrigger value="api" data-testid="tab-api" className="data-[state=active]:gold-btn text-xs uppercase tracking-[0.25em] cinzel">
              Public API
            </TabsTrigger>
          </TabsList>

          {/* QUOTES TAB */}
          <TabsContent value="quotes">
            <div className="flex flex-wrap gap-3 mb-6">
              <Button onClick={() => setShowCreate(true)} className="gold-btn text-xs uppercase tracking-[0.25em]" data-testid="new-quote-btn">
                <Plus className="w-4 h-4 mr-2" /> New Quote
              </Button>
              <Button onClick={() => setShowBulk(true)} className="gold-outline-btn bg-transparent text-xs uppercase tracking-[0.25em]" data-testid="bulk-import-btn">
                <Upload className="w-4 h-4 mr-2" /> Bulk Import
              </Button>
              <Button onClick={() => { fetchList(); fetchStats(); }} className="gold-outline-btn bg-transparent text-xs uppercase tracking-[0.25em]" data-testid="refresh-btn">
                <RefreshCw className="w-4 h-4 mr-2" /> Refresh
              </Button>
            </div>

            <Filters
              filter={filter}
              setFilter={setFilter}
              categories={categories}
              moods={moods}
            />

            <div className="text-[10px] uppercase tracking-[0.3em] text-amber-200/50 mb-4 cinzel">
              Showing {items.length} of {total}
            </div>

            {loading ? (
              <div className="text-center py-16 text-amber-200/60 text-xs uppercase tracking-[0.3em]">Loading...</div>
            ) : (
              <div className="space-y-3">
                {items.map((q) => (
                  <QuoteRow
                    key={q.id}
                    quote={q}
                    onEdit={() => setEditing(q)}
                    onDelete={() => deleteQuote(q.id)}
                    onApprove={() => setPublic(q.id, true)}
                    onReject={() => setPublic(q.id, false)}
                    onToggleUsed={() => toggleUsed(q.id, q.is_used)}
                  />
                ))}
              </div>
            )}
          </TabsContent>

          {/* GENERATE TAB */}
          <TabsContent value="generate">
            <GenerateTab
              count={generateCount}
              setCount={setGenerateCount}
              generating={generating}
              onGenerate={generateDrafts}
            />
          </TabsContent>

          {/* API TAB */}
          <TabsContent value="api">
            <ApiDocsTab />
          </TabsContent>
        </Tabs>
      </div>

      {/* Create dialog */}
      <QuoteDialog
        open={showCreate}
        onClose={() => setShowCreate(false)}
        onSave={(data) => saveQuote(data)}
        title="New Quote"
        initial={emptyQuote}
        categories={categories}
        moods={moods}
      />

      {/* Edit dialog */}
      <QuoteDialog
        open={!!editing}
        onClose={() => setEditing(null)}
        onSave={(data) => saveQuote(data, editing.id)}
        title="Edit Quote"
        initial={editing ? { ...editing, tags: (editing.tags || []).join(", ") } : emptyQuote}
        categories={categories}
        moods={moods}
      />

      {/* Bulk import */}
      <Dialog open={showBulk} onOpenChange={setShowBulk}>
        <DialogContent className="bg-black border-amber-200/25 text-white max-w-2xl">
          <DialogHeader>
            <DialogTitle className="gold-text script-font text-4xl">Bulk Import</DialogTitle>
            <DialogDescription className="text-amber-200/50 text-xs cinzel tracking-[0.2em]">
              Paste a JSON array of quote objects
            </DialogDescription>
          </DialogHeader>
          <Textarea
            data-testid="bulk-textarea"
            rows={12}
            placeholder='[{"text":"...","author":"...","category":"consciousness","tags":["soul"],"mood":"mystical","cinematic_score":85,"is_public":true}]'
            value={bulkText}
            onChange={(e) => setBulkText(e.target.value)}
            className="bg-black border-amber-200/25 text-white text-xs font-mono"
          />
          <Button onClick={bulkImport} className="gold-btn text-xs uppercase tracking-[0.25em] mt-4" data-testid="bulk-submit-btn">
            Import
          </Button>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ===== Sub-components =====
const StatsRow = ({ stats }) => (
  <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
    <StatCard label="Total" value={stats.total} />
    <StatCard label="Public" value={stats.public} accent />
    <StatCard label="Pending" value={stats.pending_approval} />
    <StatCard label="Unused" value={stats.unused_public} accent />
    <StatCard label="Used" value={stats.used} />
  </div>
);

const StatCard = ({ label, value, accent }) => (
  <div className={`border ${accent ? "border-amber-200/40" : "border-amber-200/15"} bg-black/40 backdrop-blur-sm p-6 text-center`}>
    <div className={`text-4xl font-light ${accent ? "gold-text" : "text-white"}`}>{value}</div>
    <div className="text-[10px] uppercase tracking-[0.3em] text-amber-200/50 cinzel mt-2">{label}</div>
  </div>
);

const Filters = ({ filter, setFilter, categories, moods }) => (
  <div className="grid md:grid-cols-5 gap-3 mb-6 p-4 border border-amber-200/20 bg-black/40">
    <Input
      data-testid="admin-search-input"
      placeholder="Search..."
      value={filter.search}
      onChange={(e) => setFilter((f) => ({ ...f, search: e.target.value }))}
      className="bg-black border-amber-200/25 text-white text-sm"
    />
    <Select value={filter.category} onValueChange={(v) => setFilter((f) => ({ ...f, category: v }))}>
      <SelectTrigger className="bg-black border-amber-200/25 text-white text-sm" data-testid="filter-category">
        <SelectValue />
      </SelectTrigger>
      <SelectContent className="bg-zinc-950 border-amber-200/25 max-h-72">
        <SelectItem value="all" className="text-white">All Categories</SelectItem>
        {categories.map((c) => (
          <SelectItem key={c} value={c} className="text-white">{c}</SelectItem>
        ))}
      </SelectContent>
    </Select>
    <Select value={filter.mood} onValueChange={(v) => setFilter((f) => ({ ...f, mood: v }))}>
      <SelectTrigger className="bg-black border-amber-200/25 text-white text-sm" data-testid="filter-mood">
        <SelectValue />
      </SelectTrigger>
      <SelectContent className="bg-zinc-950 border-amber-200/25 max-h-72">
        <SelectItem value="all" className="text-white">All Moods</SelectItem>
        {moods.map((m) => (
          <SelectItem key={m} value={m} className="text-white">{m}</SelectItem>
        ))}
      </SelectContent>
    </Select>
    <Select value={filter.is_public} onValueChange={(v) => setFilter((f) => ({ ...f, is_public: v }))}>
      <SelectTrigger className="bg-black border-amber-200/25 text-white text-sm" data-testid="filter-public">
        <SelectValue />
      </SelectTrigger>
      <SelectContent className="bg-zinc-950 border-amber-200/25">
        <SelectItem value="all" className="text-white">All Status</SelectItem>
        <SelectItem value="true" className="text-white">Public</SelectItem>
        <SelectItem value="false" className="text-white">Pending</SelectItem>
      </SelectContent>
    </Select>
    <Select value={filter.is_used} onValueChange={(v) => setFilter((f) => ({ ...f, is_used: v }))}>
      <SelectTrigger className="bg-black border-amber-200/25 text-white text-sm" data-testid="filter-used">
        <SelectValue />
      </SelectTrigger>
      <SelectContent className="bg-zinc-950 border-amber-200/25">
        <SelectItem value="all" className="text-white">All Usage</SelectItem>
        <SelectItem value="false" className="text-white">Unused</SelectItem>
        <SelectItem value="true" className="text-white">Used</SelectItem>
      </SelectContent>
    </Select>
  </div>
);

const QuoteRow = ({ quote, onEdit, onDelete, onApprove, onReject, onToggleUsed }) => (
  <div
    data-testid={`quote-row-${quote.id}`}
    className="border border-amber-200/15 hover:border-amber-200/40 bg-black/40 p-5 grid grid-cols-12 gap-4 items-start transition-colors"
  >
    <div className="col-span-12 md:col-span-7">
      <p className="text-sm italic text-gray-200 font-light leading-relaxed mb-2">
        &ldquo;{quote.text}&rdquo;
      </p>
      <p className="text-[11px] uppercase tracking-[0.25em] text-amber-100/90 cinzel">
        {quote.author}
        {quote.source ? <span className="text-amber-200/50 normal-case italic"> · {quote.source}</span> : null}
      </p>
    </div>
    <div className="col-span-12 md:col-span-3 flex flex-wrap gap-2 text-[10px] uppercase tracking-[0.2em]">
      <Badge>{quote.category}</Badge>
      <Badge>{quote.mood}</Badge>
      <Badge accent>★ {quote.cinematic_score}</Badge>
      {quote.is_public ? <Badge ok>Public</Badge> : <Badge warn>Pending</Badge>}
      {quote.is_used && <Badge muted>Used</Badge>}
    </div>
    <div className="col-span-12 md:col-span-2 flex flex-wrap gap-1 justify-end">
      {quote.is_public ? (
        <button onClick={onReject} className="p-2 text-amber-200/60 hover:text-rose-400" title="Unpublish" data-testid={`reject-${quote.id}`}>
          <X className="w-4 h-4" />
        </button>
      ) : (
        <button onClick={onApprove} className="p-2 text-amber-200/60 hover:text-emerald-400" title="Approve" data-testid={`approve-${quote.id}`}>
          <Check className="w-4 h-4" />
        </button>
      )}
      <button onClick={onToggleUsed} className="p-2 text-amber-200/60 hover:text-amber-200" title="Toggle used" data-testid={`used-${quote.id}`}>
        <RefreshCw className="w-4 h-4" />
      </button>
      <button onClick={onEdit} className="p-2 text-amber-200/60 hover:text-amber-200" title="Edit" data-testid={`edit-${quote.id}`}>
        <Edit className="w-4 h-4" />
      </button>
      <button onClick={onDelete} className="p-2 text-amber-200/60 hover:text-rose-400" title="Delete" data-testid={`delete-${quote.id}`}>
        <Trash2 className="w-4 h-4" />
      </button>
    </div>
  </div>
);

const Badge = ({ children, accent, ok, warn, muted }) => {
  let cls = "border-amber-200/20 text-amber-200/60";
  if (accent) cls = "border-amber-300/60 text-amber-200";
  if (ok) cls = "border-emerald-400/40 text-emerald-300";
  if (warn) cls = "border-amber-400/40 text-amber-300";
  if (muted) cls = "border-zinc-700 text-zinc-500";
  return <span className={`px-2 py-0.5 border ${cls} cinzel`}>{children}</span>;
};

const QuoteDialog = ({ open, onClose, onSave, title, initial, categories, moods }) => {
  const [data, setData] = useState(initial);
  useEffect(() => setData(initial), [initial, open]);

  const onChange = (k) => (v) =>
    setData((d) => ({ ...d, [k]: typeof v === "object" && v?.target ? v.target.value : v }));

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-black border-amber-200/25 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="gold-text script-font text-4xl">{title}</DialogTitle>
          <DialogDescription className="text-amber-200/50 text-xs cinzel tracking-[0.2em]">
            Fill the details for this quote
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 mt-4">
          <Textarea
            data-testid="dialog-text"
            placeholder="Quote text"
            value={data.text}
            onChange={onChange("text")}
            rows={3}
            className="bg-black border-amber-200/25 text-white"
          />
          <div className="grid grid-cols-2 gap-3">
            <Input data-testid="dialog-author" placeholder="Author" value={data.author} onChange={onChange("author")} className="bg-black border-amber-200/25 text-white text-sm" />
            <Input data-testid="dialog-source" placeholder="Source (optional)" value={data.source || ""} onChange={onChange("source")} className="bg-black border-amber-200/25 text-white text-sm" />
          </div>
          <Input data-testid="dialog-tags" placeholder="Tags (comma-separated)" value={data.tags || ""} onChange={onChange("tags")} className="bg-black border-amber-200/25 text-white text-sm" />
          <div className="grid grid-cols-3 gap-3">
            <Select value={data.category} onValueChange={onChange("category")}>
              <SelectTrigger className="bg-black border-amber-200/25 text-white text-sm" data-testid="dialog-category">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-zinc-950 border-amber-200/25 max-h-72">
                {categories.map((c) => (
                  <SelectItem key={c} value={c} className="text-white">{c}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={data.mood} onValueChange={onChange("mood")}>
              <SelectTrigger className="bg-black border-amber-200/25 text-white text-sm" data-testid="dialog-mood">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-zinc-950 border-amber-200/25 max-h-72">
                {moods.map((m) => (
                  <SelectItem key={m} value={m} className="text-white">{m}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Input
              data-testid="dialog-score"
              type="number"
              min={0}
              max={100}
              placeholder="Score"
              value={data.cinematic_score}
              onChange={onChange("cinematic_score")}
              className="bg-black border-amber-200/25 text-white text-sm"
            />
          </div>
          <label className="flex items-center space-x-3 text-xs uppercase tracking-[0.25em] cinzel text-amber-200/80">
            <input
              data-testid="dialog-public"
              type="checkbox"
              checked={!!data.is_public}
              onChange={(e) => onChange("is_public")(e.target.checked)}
              className="accent-amber-400"
            />
            <span>Public (approved)</span>
          </label>
          <Button onClick={() => onSave(data)} className="gold-btn w-full py-5 text-xs uppercase tracking-[0.3em] mt-4" data-testid="dialog-save-btn">
            Save
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const GenerateTab = ({ count, setCount, generating, onGenerate }) => (
  <div className="max-w-xl mx-auto border border-amber-200/25 bg-black/40 p-10 text-center space-y-6">
    <Sparkles className="w-12 h-12 text-amber-300 mx-auto" />
    <h2 className="cinzel text-2xl gold-text tracking-[0.2em] uppercase">AI Generate</h2>
    <p className="text-xs text-amber-200/60 leading-relaxed">
      Generate cinematic wisdom quotes via Gemini. Output is saved as <strong>drafts</strong> requiring approval. The weekly cron runs every Monday at 09:00 UTC.
    </p>
    <div className="flex items-center justify-center space-x-3">
      <Input
        data-testid="generate-count-input"
        type="number"
        min={5}
        max={50}
        value={count}
        onChange={(e) => setCount(e.target.value)}
        className="bg-black border-amber-200/25 text-white w-24 text-center"
      />
      <span className="text-[10px] uppercase tracking-[0.3em] text-amber-200/50 cinzel">drafts</span>
    </div>
    <Button
      data-testid="generate-btn"
      onClick={onGenerate}
      disabled={generating}
      className="gold-btn px-12 py-6 text-xs uppercase tracking-[0.3em]"
    >
      {generating ? "Generating... (≈15s)" : "Generate Drafts"}
    </Button>
  </div>
);

const ApiDocsTab = () => {
  const baseUrl = useMemo(() => process.env.REACT_APP_BACKEND_URL || "", []);
  return (
    <div className="max-w-3xl space-y-6 text-sm font-light">
      <Section title="Public API for Automation">
        <p className="text-amber-200/60 leading-relaxed mb-4">
          Use these endpoints from your AI video pipeline. <strong>Always pass the automation token</strong> — these endpoints are not for end-user browsers.
        </p>
        <Code>
{`GET ${baseUrl}/api/public/quotes?token=YOUR_TOKEN

Optional filters:
  &category=consciousness
  &mood=mystical
  &min_score=80
  &unused_only=true
  &limit=50`}
        </Code>
      </Section>

      <Section title="Daily Quote (Recommended)">
        <Code>
{`GET ${baseUrl}/api/public/quotes/daily?token=YOUR_TOKEN&mark_used=true

Returns one unused, approved, high-score quote.
When all are used, the cycle automatically resets.`}
        </Code>
      </Section>

      <Section title="Alternative: Header auth">
        <Code>{`curl -H "X-API-Token: YOUR_TOKEN" ${baseUrl}/api/public/quotes/daily`}</Code>
      </Section>

      <Section title="Security">
        <ul className="list-disc list-inside text-amber-200/60 space-y-2">
          <li>The token is set in backend <code>.env</code> as <code>AUTOMATION_API_TOKEN</code></li>
          <li>Public endpoints only return approved (<code>is_public=true</code>) quotes</li>
          <li>Admin endpoints require admin email + JWT login — never exposed to the pipeline</li>
        </ul>
      </Section>
    </div>
  );
};

const Section = ({ title, children }) => (
  <div className="border border-amber-200/20 bg-black/40 p-6">
    <h3 className="cinzel text-sm uppercase tracking-[0.3em] gold-text mb-4">{title}</h3>
    {children}
  </div>
);

const Code = ({ children }) => (
  <pre className="bg-zinc-950 border border-amber-200/15 p-4 overflow-x-auto text-xs text-amber-100/80 font-mono">
{children}
  </pre>
);

export default AdminPage;
